import { Icon } from '@iconify/react';
import { Input } from '@nextui-org/react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

interface SearchInputProps {
  initialValue?: string;
  placeholder?: string;
  /** Delay after the last keystroke before `onSearch` fires; 0 = immediately. */
  delay: number;
  onSearch: (value: string) => void;
}

/**
 * Search box that owns its own text state. With a `delay`, a keystroke
 * re-renders only the input and the table is told about the new term once
 * typing pauses; with no delay it reports every keystroke, like a plain input.
 */
function SearchInput({
  initialValue = '',
  placeholder,
  delay,
  onSearch,
}: SearchInputProps) {
  const [value, setValue] = useState(initialValue);

  // Keep the debouncer stable even if the parent passes a new callback.
  const onSearchRef = useRef(onSearch);
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  const emitSearch = useCallback((term: string) => {
    onSearchRef.current(term);
  }, []);
  const debouncedSearch = useDebounceCallback(emitSearch, delay);

  const handleValueChange = (next: string) => {
    setValue(next);
    if (delay > 0) {
      debouncedSearch(next);
    } else {
      emitSearch(next);
    }
  };

  const handleClear = () => {
    debouncedSearch.cancel();
    setValue('');
    emitSearch('');
  };

  return (
    <Input
      isClearable
      className="w-full"
      placeholder={placeholder || 'Search...'}
      startContent={<Icon icon="mdi:search" />}
      value={value}
      onClear={handleClear}
      onValueChange={handleValueChange}
    />
  );
}

export default memo(SearchInput);
